import { useQuery } from "@tanstack/react-query";
import { Badge } from "@tcg/ui";
import { FileText, Printer } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { MoneyText, useYen } from "@/components/MoneyText";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { call } from "@/lib/api";
import type { ShiftPage } from "@/lib/types";

import { readReport } from "./report";
import { ReportView } from "./ReportView";
import { printShiftReport, useShiftHistory } from "./useShift";

type ShiftRow = ShiftPage["data"][number];

function when(value: string | null | undefined, locale: string): string {
  if (!value) {
    return "";
  }
  return new Intl.DateTimeFormat(locale === "ja" ? "ja-JP" : locale === "th" ? "th-TH" : "en-GB", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  }).format(new Date(value));
}

/** レジ開閉の履歴 (T07-26): the register's shifts, newest first → the Z report (view / print / PDF). */
export function ShiftHistory({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const yen = useYen();
  const history = useShiftHistory(true);
  const [open, setOpen] = useState<ShiftRow | null>(null);
  const locale = i18n.resolvedLanguage ?? "ja";
  const rows = history.data?.data ?? [];
  return (
    <section aria-labelledby="shift-history" className="flex h-full min-h-0 flex-col gap-3 p-4">
      <header className="flex items-center gap-3">
        <h1 id="shift-history" className="flex-1 text-2xl font-semibold">
          {t("shift.history")}
        </h1>
        <button
          type="button"
          onClick={onBack}
          className="min-h-pos-touch rounded-md border px-5 text-lg"
        >
          {t("app.back")}
        </button>
      </header>
      {rows.length === 0 && !history.isLoading ? (
        <p className="text-muted-foreground">{t("shift.history_empty")}</p>
      ) : null}
      <ul className="flex min-h-0 flex-col gap-2 overflow-y-auto">
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              disabled={row.status !== "closed"}
              onClick={() => setOpen(row)}
              className="grid min-h-pos-touch w-full grid-cols-[1fr_auto_auto] items-center gap-3 rounded-md border px-4 py-2 text-left hover:bg-accent disabled:opacity-80"
            >
              <span>
                {when(row.openedAt, locale)} – {row.closedAt ? when(row.closedAt, locale) : ""}{" "}
                <span className="text-muted-foreground">
                  {row.openedByName ?? ""}
                  {row.closedByName && row.closedByName !== row.openedByName
                    ? ` / ${row.closedByName}`
                    : ""}
                </span>
              </span>
              {row.status === "open" ? (
                <Badge variant="pending">{t("shift.status_open")}</Badge>
              ) : row.variance ? (
                <Badge variant="problem">
                  {t("shift.variance")} {row.variance < 0 ? "−" : "+"}
                  {yen(Math.abs(row.variance))}
                </Badge>
              ) : (
                <Badge variant="outline">{t("shift.variance_none")}</Badge>
              )}
              {row.countedCash !== null && row.countedCash !== undefined ? (
                <MoneyText amount={row.countedCash} />
              ) : (
                <span />
              )}
            </button>
          </li>
        ))}
      </ul>
      {open ? <ZReportModal shift={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function ZReportModal({ shift, onClose }: { shift: ShiftRow; onClose: () => void }) {
  const { t } = useTranslation();
  const { api, printer } = usePos();
  const toast = useToast();
  const report = useQuery({
    queryKey: ["pos-shifts", "detail", shift.id, "z"],
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/shifts/{shift_id}/z-report", {
          params: { path: { shift_id: shift.id } },
        }),
      ),
    staleTime: Infinity,
  });
  const pdf = useQuery({
    queryKey: ["pos-shifts", "detail", shift.id, "z-pdf"],
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/shifts/{shift_id}/z-report", {
          params: { path: { shift_id: shift.id }, query: { format: "pdf" } },
        }),
      ),
  });
  const data = report.data && "report" in report.data ? readReport(report.data.report) : null;
  const pdfData = pdf.data && "status" in pdf.data && pdf.data.format === "pdf" ? pdf.data : null;
  const print = async () => {
    const ok = await printShiftReport({ api, printer, shiftId: shift.id, kind: "z" });
    toast.show(ok ? t("shift.printed") : t("shift.print_failed"), ok ? "success" : "error");
  };
  return (
    <Modal
      open
      size="lg"
      title={t("report.title_z")}
      onClose={onClose}
      footer={
        <>
          {pdfData?.status === "issued" && pdfData.url ? (
            <a
              href={pdfData.url}
              target="_blank"
              rel="noreferrer"
              className="flex min-h-pos-touch items-center gap-2 rounded-md border px-6 text-lg"
            >
              <FileText aria-hidden />
              {t("shift.pdf")}
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => void print()}
            className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground"
          >
            <Printer aria-hidden />
            {t("shift.print_z")}
          </button>
        </>
      }
    >
      {data ? <ReportView report={data} /> : <p>{t("app.loading")}</p>}
    </Modal>
  );
}
